import { AppProfile } from '../../../components/app/App';
import { useState, useEffect, useRef, useCallback } from 'react';

import LayoutChat from '../../../components/layout/LayoutChat';
import ChatHeader from '../../../components/module/Chat/ChatHeader';
import ChatFooter from '../../../components/module/Chat/ChatFooter';
import ChatMessages from '../../../components/module/Chat/ChatMessages';

import ChatBubble2 from '../../../components/module/Chat/Bubble/ChatBubble2';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMessages } from '../../../hooks/messages/useMessages';
import { useGroupMessages } from '../../../hooks/messages/useGroupMessages';
import { Box, CircularProgress } from '@mui/joy';
import ServerError from '../../../components/module/ServerError/ServerError';

import { apiFetch } from '../../../utils/apiClient';
import { apiSendMessage } from '../../../utils/apiSendMessage';
import { useSocket } from '../../../components/app/SocketProvider';

function PageChat({ isGroup = false }) {
    const [chatAttachments, setChatAttachments] = useState([]);

    const socket = useSocket();
    const { id, chatId } = useParams();
    // ?messageId= приходит из поиска по сообщениям — открываем переписку сразу
    // на найденном сообщении.
    const [searchParams] = useSearchParams();

    const regularChat = useMessages(isGroup ? null : id);
    const groupChat = useGroupMessages(isGroup ? chatId : null);

    const { messages, user, currentUserId, loading, error } = isGroup
        ? { messages: groupChat.messages, user: null, currentUserId: groupChat.currentUserId, loading: groupChat.loading, error: groupChat.error }
        : regularChat;

    const groupChatInfo = isGroup ? groupChat.chat : null;

    // Ограничения (блок/приватность) — только для личных чатов.
    // Храним в state, чтобы обновлять в реальном времени при блокировке.
    const [restriction, setRestriction] = useState({ blocked: false, canMessage: true, canCall: true });

    useEffect(() => {
        if (isGroup) {
            setRestriction({ blocked: false, canMessage: true, canCall: true });
            return;
        }
        setRestriction(regularChat?.restriction ?? { blocked: false, canMessage: true, canCall: true });
    }, [isGroup, regularChat?.restriction]);

    // Локальное обновление при блокировке/разблокировке из меню чата
    const handleBlockChange = (blocked) => {
        setRestriction(blocked
            ? { blocked: true, canMessage: false, canCall: false }
            : { blocked: false, canMessage: true, canCall: true });
    };

    // Реальное время: собеседник заблокировал/разблокировал меня
    useEffect(() => {
        if (!socket || isGroup) return;

        const handleBlockEvent = ({ fromUserId, blocked }) => {
            if (Number(fromUserId) !== Number(id)) return;
            handleBlockChange(!!blocked);
        };

        socket.on('block:changed', handleBlockEvent);
        return () => socket.off('block:changed', handleBlockEvent);
    }, [socket, isGroup, id]);

    const [showMessages, setShowMessages] = useState();
    const [isPartnerTyping, setIsPartnerTyping] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    const [replyTo, setReplyTo] = useState(null);

    const currentUserIdRef = useRef(currentUserId);
    const currentChatIdRef = useRef(null);

    useEffect(() => { currentUserIdRef.current = currentUserId; }, [currentUserId]);
    useEffect(() => {
        currentChatIdRef.current = isGroup ? parseInt(chatId) : regularChat?.chatId ?? null;
    }, [isGroup, chatId, regularChat?.chatId]);

    useEffect(() => {
        setShowMessages(messages);
    }, [messages]);

    useEffect(() => {
        const resolvedChatId = isGroup ? chatId : regularChat?.chatId;
        if (!resolvedChatId) return;
        apiFetch(`/api/chats/${resolvedChatId}/read`, { method: 'POST' }).catch(() => { });
    }, [regularChat?.chatId, chatId, isGroup]);

    const sendMessage = async (text = '') => {
        const trimmed = text.trim();
        if (!trimmed && chatAttachments.length === 0) return;

        const localAttachmentPreviews = chatAttachments.map(file => ({
            url: URL.createObjectURL(file),
            type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file',
            name: file.name,
            size: file.size,
            _local: true,
        }));

        const currentReply = replyTo;
        const optimisticMsg = {
            fromMe: true,
            content: trimmed,
            attachments: localAttachmentPreviews.length > 0 ? localAttachmentPreviews : null,
            created_at: new Date(),
            checkStatus: -1,
            replyPreview: currentReply ? { id: currentReply.id, content: currentReply.content } : null,
        };

        setShowMessages(prev => [...prev, optimisticMsg]);
        setChatAttachments([]);
        setReplyTo(null);

        const formData = new FormData();
        if (trimmed) formData.append('content', trimmed);
        for (const file of chatAttachments) formData.append('files', file);
        if (currentReply) formData.append('replyToId', currentReply.id);

        const updateLastOptimistic = (extra = {}) => {
            setShowMessages(prev => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.fromMe && last.checkStatus === -1) {
                    copy[copy.length - 1] = { ...last, checkStatus: 1, ...extra };
                }
                return copy;
            });
        };

        try {
            if (isGroup) {
                const res = await apiFetch(`/api/chats/group/${chatId}/send`, {
                    method: 'POST',
                    body: formData,
                });
                const data = await res.json();
                updateLastOptimistic({
                    ...(data?.messageId ? { id: data.messageId } : {}),
                    ...(data?.attachments ? { attachments: data.attachments, _local: false } : {}),
                    ...(data?.replyPreview ? { replyPreview: data.replyPreview } : {}),
                });
            } else {
                const res = await apiFetch(`/api/messages/${id}/send`, {
                    method: 'POST',
                    body: formData,
                });
                const data = await res.json();
                updateLastOptimistic({
                    ...(data?.messageId ? { id: data.messageId } : {}),
                    ...(data?.attachments ? { attachments: data.attachments, _local: false } : {}),
                    ...(data?.replyPreview ? { replyPreview: data.replyPreview } : {}),
                });
            }
        } catch (e) {
            console.error('Ошибка отправки:', e);
            setShowMessages(prev => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.fromMe && last.checkStatus === -1) {
                    copy[copy.length - 1] = { ...last, checkStatus: -2 };
                }
                return copy;
            });
        }
    };

    // Подгрузка истории вверх (только личные чаты). Старые сообщения добавляем
    // в начало showMessages сами, чтобы не перетереть оптимистичные/сокетные.
    const handleLoadOlder = useCallback(async () => {
        if (isGroup || !regularChat?.loadOlder) return;
        const older = await regularChat.loadOlder();
        if (older && older.length) {
            setShowMessages(prev => [...older, ...(prev ?? [])]);
        }
    }, [isGroup, regularChat]);

    const handleTypingEmit = (isTyping) => {
        if (!socket || isGroup) return;
        socket.emit('typing', { toUserId: id, isTyping });
    };

    const handleDelete = async (messageId, deleteType) => {
        setShowMessages(prev => prev.filter(m => m.id !== messageId));

        try {
            await apiFetch(`/api/messages/message/${messageId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deleteType }),
            });
        } catch (e) {
            console.error('Ошибка удаления', e);
        }
    };

    const handleEdit = (messageId, text) => {
        setEditingId(messageId);
        setEditText(text);
    };

    const handleEditSave = async (savedText) => {
        if (!savedText.trim()) return;

        const savedId = editingId;
        setEditingId(null);
        setEditText('');

        setShowMessages(prev =>
            prev.map(m => m.id === savedId
                ? { ...m, content: savedText, edited: true }
                : m
            )
        );

        try {
            await apiFetch(`/api/messages/message/${savedId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content: savedText }),
            });
        } catch (e) {
            console.error('Ошибка редактирования:', e);
        }
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditText('');
    };

    const handleReply = (msg) => {
        setReplyTo(msg);
        setEditingId(null);
        setEditText('');
    };

    const handleReplyCancel = () => setReplyTo(null);

    const getTitle = () => {
        if (isGroup) return groupChatInfo?.chatName ?? 'Групповой чат';
        return user?.firstName ? user.firstName : 'пользователем';
    };

    useEffect(() => {
        if (!socket) return;

        const handleReceiveMessage = (payload) => {
            if (!isGroup && parseInt(payload.senderId) === parseInt(id)) {
                setShowMessages((prev) => [
                    ...prev,
                    {
                        id: payload.id,
                        fromMe: false,
                        content: payload.content,
                        attachments: payload.attachments ?? null,
                        created_at: payload.created_at,
                        replyPreview: payload.replyPreview ?? null,
                        forwardedFromUser: payload.forwardedFromUser ?? null,
                    },
                ]);

                if (payload.chatId) {
                    apiFetch(`/api/chats/${payload.chatId}/read`, { method: 'POST' }).catch(() => { });
                }
            }
        };

        const handleReceiveGroupMessage = (payload) => {
            if (isGroup && parseInt(payload.chatId) === parseInt(chatId)) {
                setShowMessages((prev) => [
                    ...prev,
                    {
                        id: payload.id,
                        fromMe: false,
                        senderId: payload.senderId,
                        senderUser: payload.user,
                        content: payload.content,
                        attachments: payload.attachments ?? null,
                        created_at: payload.created_at,
                        checkStatus: 1,
                        replyPreview: payload.replyPreview ?? null,
                        forwardedFromUser: payload.forwardedFromUser ?? null,
                    },
                ]);
            }
        };

        const handleMessageEdited = (payload) => {
            setShowMessages((prev) =>
                prev.map((msg) =>
                    msg.id === payload.messageId
                        ? { ...msg, content: payload.content, edited: true }
                        : msg
                )
            );
        };

        const handleMessageDeleted = ({ messageId }) => {
            setShowMessages(prev => prev.filter(m => m.id !== messageId));
        };

        const handleTyping = ({ fromUserId, isTyping }) => {
            if (isGroup) return;
            if (parseInt(fromUserId) !== parseInt(id)) return;
            setIsPartnerTyping(isTyping);
        };

        const handleMessagesRead = ({ chatId: readChatId, readBy }) => {
            if (parseInt(readBy) === parseInt(currentUserIdRef.current)) return;
            if (parseInt(readChatId) !== parseInt(currentChatIdRef.current)) return;
            setShowMessages(prev => prev.map(msg =>
                msg.fromMe && msg.checkStatus === 1 ? { ...msg, checkStatus: 3 } : msg
            ));
        };

        socket.on('receive_message', handleReceiveMessage);
        socket.on('receive_group_message', handleReceiveGroupMessage);
        socket.on('message_edited', handleMessageEdited);
        socket.on('message_deleted', handleMessageDeleted);
        socket.on('typing', handleTyping);
        socket.on('messages_read', handleMessagesRead);

        return () => {
            socket.off('receive_message', handleReceiveMessage);
            socket.off('receive_group_message', handleReceiveGroupMessage);
            socket.off('message_edited', handleMessageEdited);
            socket.off('message_deleted', handleMessageDeleted);
            socket.off('typing', handleTyping);
            socket.off('messages_read', handleMessagesRead);
        };
    }, [socket, id, chatId, isGroup]);

    const isReady = isGroup
        ? groupChatInfo && currentUserId
        : messages && user && currentUserId;

    return (
        <AppProfile title={`Чат с ${getTitle()}`} desc={`Переписка в Аквариум`} nonav>
            <LayoutChat>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <ServerError title="Ошибка при получении сообщений" />
                ) : isReady ? (
                    <>
                        <ChatHeader
                            user={user ?? {}}
                            currentUserId={currentUserId ?? 0}
                            isGroup={isGroup}
                            groupName={groupChatInfo?.chatName ?? ''}
                            groupImage={groupChatInfo?.chatImage ?? ''}
                            groupMembers={groupChatInfo?.users ?? []}
                            chatId={isGroup ? chatId : (regularChat?.chatId ?? null)}
                            isPartnerTyping={isPartnerTyping}
                            canCall={restriction.canCall}
                            onBlockChange={handleBlockChange}
                        />

                        <ChatMessages
                            messages={showMessages}
                            Bubble={ChatBubble2}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onReply={handleReply}
                            isGroup={isGroup}
                            bookmark={user?.bookmark ?? false}
                            blocked={restriction.blocked}
                            currentUserId={currentUserId}
                            onLoadOlder={handleLoadOlder}
                            hasMoreOlder={isGroup ? false : regularChat?.hasMore}
                            loadingOlder={isGroup ? false : regularChat?.loadingOlder}
                            jumpToMessageId={searchParams.get('messageId')}
                        />
                    </>
                ) : null}

                <ChatFooter
                    canMessage={restriction.canMessage}
                    sendMessage={sendMessage}
                    attachments={chatAttachments}
                    setAttachments={setChatAttachments}
                    editingId={editingId}
                    editText={editText}
                    setEditText={setEditText}
                    onEditSave={handleEditSave}
                    onEditCancel={handleEditCancel}
                    onTyping={handleTypingEmit}
                    replyTo={replyTo}
                    onReplyCancel={handleReplyCancel}
                />
            </LayoutChat>
        </AppProfile>
    );
}

export default PageChat;
